import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LogEntry, LogFilter, TaskTab } from '../types';

export const STORAGE_LOGS = 'atlas-ide:v2:logs';

function safeParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function scheduleLocalStorageSave(
  timers: React.MutableRefObject<Record<string, number>>,
  key: string,
  value: unknown
) {
  if (timers.current[key]) {
    window.clearTimeout(timers.current[key]);
  }
  timers.current[key] = window.setTimeout(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('localStorage save error', error);
    }
  }, 400);
}

type LogsContextValue = {
  logs: LogEntry[];
  setLogs: React.Dispatch<React.SetStateAction<LogEntry[]>>;
  addLog: (log: Omit<LogEntry, 'id' | 'timestamp'>) => void;
  clearLogs: () => void;
  clearTaskLogs: (task: TaskTab) => void;
  logFilter: LogFilter;
  setLogFilter: (filter: LogFilter) => void;
  taskTab: TaskTab;
  setTaskTab: (tab: TaskTab) => void;
  filteredLogs: LogEntry[];
  taskFilteredLogs: LogEntry[];
  lintSummary: { errors: number; warnings: number };
  setLintSummary: React.Dispatch<React.SetStateAction<{ errors: number; warnings: number }>>;
};

const LogsContext = createContext<LogsContextValue | null>(null);

export function LogsProvider({ children }: { children: React.ReactNode }) {
  const [logs, setLogs] = useState<LogEntry[]>(
    () => safeParse<LogEntry[]>(localStorage.getItem(STORAGE_LOGS)) || []
  );
  const [logFilter, setLogFilter] = useState<LogFilter>('all');
  const [taskTab, setTaskTab] = useState<TaskTab>('all');
  const [lintSummary, setLintSummary] = useState<{ errors: number; warnings: number }>({
    errors: 0,
    warnings: 0,
  });

  const persistTimers = useRef<Record<string, number>>({});

  // Persist logs
  useEffect(() => {
    scheduleLocalStorageSave(persistTimers, STORAGE_LOGS, logs);
  }, [logs]);

  // Listen for console messages from preview iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate message format
      if (!event.data || event.data.type !== 'preview-console') return;

      const { method, args } = event.data as {
        type: string;
        method: 'log' | 'warn' | 'error' | 'info' | 'debug';
        args: string[];
        timestamp: number;
      };

      // Map console method to log level
      const levelMap: Record<string, LogEntry['level']> = {
        log: 'info',
        info: 'info',
        debug: 'info',
        warn: 'warn',
        error: 'error',
      };

      const level = levelMap[method] || 'info';
      const message = args.join(' ');

      // Add log entry with preview source indicator
      setLogs((prev) => [
        ...prev,
        {
          id: `preview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          timestamp: new Date().toLocaleTimeString(),
          level,
          source: 'preview',
          message: `[Preview] ${message}`,
        },
      ]);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const addLog = useCallback((log: Omit<LogEntry, 'id' | 'timestamp'>) => {
    const entry: LogEntry = {
      ...log,
      id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toLocaleTimeString(),
    };
    setLogs((prev) => [...prev, entry]);
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const clearTaskLogs = useCallback((task: TaskTab) => {
    if (task === 'all') {
      setLogs([]);
    } else if (task === 'preview') {
      setLogs((prev) => prev.filter((l) => l.source !== 'preview'));
    } else {
      setLogs((prev) => prev.filter((l) => l.task !== task));
    }
  }, []);

  const filteredLogs = useMemo(() => {
    if (logFilter === 'all') return logs;
    return logs.filter((l) => l.level === logFilter);
  }, [logFilter, logs]);

  // Filter by task tab first, then by log level
  const taskFilteredLogs = useMemo(() => {
    let filtered = logs;

    // Filter by task tab
    if (taskTab !== 'all') {
      if (taskTab === 'preview') {
        filtered = filtered.filter((l) => l.source === 'preview');
      } else {
        filtered = filtered.filter((l) => l.task === taskTab);
      }
    }

    // Then filter by log level
    if (logFilter !== 'all') {
      filtered = filtered.filter((l) => l.level === logFilter);
    }

    return filtered;
  }, [logs, taskTab, logFilter]);

  const value = useMemo<LogsContextValue>(
    () => ({
      logs,
      setLogs,
      addLog,
      clearLogs,
      clearTaskLogs,
      logFilter,
      setLogFilter,
      taskTab,
      setTaskTab,
      filteredLogs,
      taskFilteredLogs,
      lintSummary,
      setLintSummary,
    }),
    [logs, addLog, clearLogs, clearTaskLogs, logFilter, taskTab, filteredLogs, taskFilteredLogs, lintSummary]
  );

  return <LogsContext.Provider value={value}>{children}</LogsContext.Provider>;
}

export function useLogs() {
  const context = useContext(LogsContext);
  if (!context) {
    throw new Error('useLogs must be used within a LogsProvider');
  }
  return context;
}
